import EmailAndPaymentError from '@/classes/SubmissionStatuses/EverplansStatuses/EmailAndPaymentError'
import { EmailError } from '@/classes/SubmissionStatuses/EverplansStatuses/EmailError'
import { EmailMissing } from '@/classes/SubmissionStatuses/EverplansStatuses/EmailMissing'
import { ManuallyResolvedStatus } from '@/classes/SubmissionStatuses/EverplansStatuses/ManuallyResolvedStatus'
import PaymentError from '@/classes/SubmissionStatuses/EverplansStatuses/PaymentError'
import { PendingStatus } from '@/classes/SubmissionStatuses/EverplansStatuses/PendingStatus'
import { SuccessStatus } from '@/classes/SubmissionStatuses/EverplansStatuses/SuccessStatus'
import { FailedSendStatus } from '@/classes/SubmissionStatuses/FailedSendStatus'
import { NoneSentStatus } from '@/classes/SubmissionStatuses/NoneSentStatus'
import type StatusObject from '@/classes/SubmissionStatuses/StatusObject'
import {
	type LocalSubmissionStatus,
	SubmissionStatusMapper,
} from '@/classes/SubmissionStatuses/SubmissionStatusMapper'
import { UnknownError } from '@/classes/SubmissionStatuses/UnknownError'
import { UnknownStatus } from '@/classes/SubmissionStatuses/UnknownStatus'
import {
	EverplansEmailStatusEnum,
	type EverplansSubmissionStatus,
	type SubmissionAction,
} from '@/classes/SubmissionStatuses/types'

export enum EverplansStatus {
	PENDING = 'pending',
	COMPLETED = 'completed',
	FAILED = 'failed',
	MANUALLY_RESOLVED = 'manually_resolved',
}

enum EverplansPaymentStatus {
	SUCCEEDED = 'succeeded',
	CARD_FAILED = 'card_failed',
	PENDING = 'pending',
	NOT_APPLICABLE = 'not_applicable',
}

export class EverplansStatusMapper extends SubmissionStatusMapper {
	submissionStatusObject: StatusObject
	constructor(
		submissionStatus?: EverplansSubmissionStatus,
		localSubmissionStatus: LocalSubmissionStatus[] = []
	) {
		super(localSubmissionStatus)
		this.submissionStatusObject = this.toStatusObject(submissionStatus)
	}

	toStatusObject(submissionStatus?: EverplansSubmissionStatus): StatusObject {
		const failedChannels = this.getFailedOutgoingChannels(['Everplans'])

		if (failedChannels.length && !submissionStatus) {
			return new FailedSendStatus('Everplans', failedChannels)
		}

		if (!submissionStatus) {
			return new NoneSentStatus('Everplans')
		}

		if (this.isPending(submissionStatus)) {
			return new PendingStatus(submissionStatus)
		} else if (this.isManuallyResolved(submissionStatus)) {
			return new ManuallyResolvedStatus(submissionStatus)
		} else if (this.isSuccessful(submissionStatus)) {
			return new SuccessStatus(submissionStatus)
		} else if (
			this.emailFailed(submissionStatus) &&
			this.paymentFailed(submissionStatus)
		) {
			return new EmailAndPaymentError(submissionStatus)
		} else if (this.emailMissing(submissionStatus)) {
			return new EmailMissing(submissionStatus)
		} else if (this.emailFailed(submissionStatus)) {
			return new EmailError(submissionStatus)
		} else if (this.paymentFailed(submissionStatus)) {
			return new PaymentError(submissionStatus)
		} else if (submissionStatus.status === EverplansStatus.FAILED) {
			return new UnknownError(submissionStatus, 'Everplans')
		} else {
			return new UnknownStatus(submissionStatus)
		}
	}

	/**
	 * For old records we assume payment status is not failed if it is undefined.
	 * If EFT payment was invalid, we also expect a Basic tier and 'not_applicable' status,
	 * but it should be a payment failure because they requested premium and got basic
	 * @private
	 */
	private paymentFailed(submissionStatus: EverplansSubmissionStatus): boolean {
		// There are some special cases where they can be premium even if payment failed
		// in those cases we don't want to prompt for payment any more.
		if (submissionStatus.accountTier === 'premium') {
			return false
		}

		// If we don't know their requested account tier this is legacy and we don't
		// have enough info to say payment failed
		if (!submissionStatus.requestedAccountTier) {
			return false
		}

		// If they requested premium but got freemium something went wrong
		// This covers our special case with ACH/EFT payments.
		if (
			submissionStatus.requestedAccountTier === 'premium' &&
			submissionStatus.accountTier === 'freemium'
		) {
			return true
		}

		return submissionStatus.paymentStatus === EverplansPaymentStatus.CARD_FAILED
	}

	/**
	 * We only consider it missing if requested tier was basic
	 */
	private emailMissing(submissionStatus: EverplansSubmissionStatus): boolean {
		return (
			submissionStatus.requestedAccountTier === 'freemium' &&
			submissionStatus.emailStatus?.status === EverplansEmailStatusEnum.BLANK
		)
	}

	/**
	 * The email status must be present for us to assert that it failed.
	 */
	private emailFailed(submissionStatus: EverplansSubmissionStatus): boolean {
		return (
			!!submissionStatus.emailStatus?.status &&
			submissionStatus.emailStatus?.status !== EverplansEmailStatusEnum.VALID
		)
	}

	private isPending(submissionStatus: EverplansSubmissionStatus): boolean {
		return submissionStatus.status === EverplansStatus.PENDING
	}

	private isManuallyResolved(
		submissionStatus: EverplansSubmissionStatus
	): boolean {
		return submissionStatus.status === EverplansStatus.MANUALLY_RESOLVED
	}

	/**
	 * To account for statuses from before the V2 status update we need to
	 * assume that if paymentStatus and emailStatus are not present, then there
	 * were no errors.
	 * @private
	 */
	private isSuccessful(submissionStatus: EverplansSubmissionStatus): boolean {
		return (
			submissionStatus.status === EverplansStatus.COMPLETED &&
			!this.emailFailed(submissionStatus) &&
			!this.paymentFailed(submissionStatus)
		)
	}

	getVendorName(): string | undefined {
		return this.submissionStatusObject.getVendorName()
	}

	getThemeVariant() {
		return this.submissionStatusObject.getThemeVariant()
	}

	getActions(): Array<SubmissionAction> {
		return this.submissionStatusObject.getActions()
	}

	getStatusText(): string {
		return this.submissionStatusObject.getStatusText()
	}

	getToolTipText(): { title: string; body: string } {
		return this.submissionStatusObject.getToolTipText()
	}

	getUpdatedAt(): string {
		return this.submissionStatusObject.getUpdatedAt()
	}
}
